import React, { useState, useEffect, useCallback } from 'react';
import { incidentAPI, cityAPI } from '../services/api';
import { Incident, City, IncidentStatus, IncidentPriority } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Collapse,
  Stack
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Refresh, 
  FilterList, 
  Clear, 
  Search, 
  ExpandMore,
  Visibility,
  Assignment,
  BugReport,
  Warning,
  CheckCircle,
  Cancel,
  Schedule,
  Info
} from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Incidents: React.FC = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingIncidentId, setEditingIncidentId] = useState<string | null>(null);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    issueType: '',
    platform: '',
    cityId: '',
    userEmail: '',
    searchTerm: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [expandedIncident, setExpandedIncident] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    issueType: '',
    userEmail: '',
    description: '',
    platform: 'mobile',
    cityId: '',
    adminNotes: '',
    status: 'OPEN' as IncidentStatus,
    priority: 'MEDIUM' as IncidentPriority
  });
  
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Load incidents
  const loadIncidents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (filters.status || filters.priority || filters.issueType || filters.platform || filters.cityId || filters.userEmail) {
        // Use search API with filters
        const searchParams: any = {};
        if (filters.status) searchParams.status = filters.status;
        if (filters.priority) searchParams.priority = filters.priority;
        if (filters.issueType) searchParams.issueType = filters.issueType;
        if (filters.platform) searchParams.platform = filters.platform;
        if (filters.cityId) searchParams.cityId = filters.cityId;
        
        response = await incidentAPI.search(searchParams);
      } else {
        response = await incidentAPI.getAll();
      }
      
      let filteredIncidents = response.data;
      
      // Apply client-side search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredIncidents = filteredIncidents.filter(incident =>
          incident.issueType?.toLowerCase().includes(searchLower) ||
          incident.description?.toLowerCase().includes(searchLower) ||
          incident.userEmail?.toLowerCase().includes(searchLower) ||
          incident.id?.toLowerCase().includes(searchLower)
        );
      }
      
      setIncidents(filteredIncidents);
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load incidents');
      console.error('Error loading incidents:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load cities
  const loadCities = useCallback(async () => {
    try {
      const response = await cityAPI.getAll();
      setCities(response.data);
    } catch (err) {
      console.error('Error loading cities:', err);
    }
  }, []);


  useEffect(() => {
    loadIncidents();
    loadCities();
  }, [loadIncidents, loadCities]);

  // Handle filter changes
  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      issueType: '',
      platform: '',
      cityId: '',
      userEmail: '',
      searchTerm: ''
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    try {
      if (isEditMode && editingIncidentId) {
        // Update existing incident
        if (formData.status !== selectedIncident?.status) {
          await incidentAPI.updateStatus(editingIncidentId, { status: formData.status });
        }
        if (formData.priority !== selectedIncident?.priority) {
          await incidentAPI.updatePriority(editingIncidentId, { priority: formData.priority });
        }
        if (formData.adminNotes && formData.adminNotes.trim()) {
          await incidentAPI.addNotes(editingIncidentId, { adminNotes: formData.adminNotes });
        }
      } else {
        // Create new incident
        await incidentAPI.create({
          issueType: formData.issueType,
          userEmail: formData.userEmail || undefined,
          description: formData.description,
          platform: formData.platform,
          cityId: formData.cityId || undefined
        });
      }

      setShowModal(false);
      resetForm();
      await loadIncidents();
    } catch (err) {
      const error = err as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save incident');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      issueType: '',
      userEmail: '',
      description: '',
      platform: 'mobile',
      cityId: '',
      adminNotes: '',
      status: 'OPEN',
      priority: 'MEDIUM'
    });
    setFormError(null);
    setIsEditMode(false);
    setEditingIncidentId(null);
    setSelectedIncident(null);
  };

  // Handle edit
  const handleEdit = (incident: Incident) => {
    setSelectedIncident(incident);
    setFormData({
      issueType: incident.issueType || '',
      userEmail: incident.userEmail || '',
      description: incident.description || '',
      platform: incident.platform || 'mobile',
      cityId: incident.cityId || '',
      adminNotes: '',
      status: incident.status || 'OPEN',
      priority: incident.priority || 'MEDIUM'
    });
    setIsEditMode(true);
    setEditingIncidentId(incident.id || null);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this incident?')) {
      return;
    }

    try {
      await incidentAPI.delete(id);
      await loadIncidents();
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete incident');
    }
  };

  // Handle resolve
  const handleResolve = async (id: string, adminNotes: string) => {
    try {
      await incidentAPI.resolve(id, { adminNotes });
      await loadIncidents();
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to resolve incident');
    }
  };

  // Get status color
  const getStatusColor = (status: IncidentStatus) => {
    switch (status) {
      case 'OPEN': return 'error';
      case 'IN_PROGRESS': return 'warning';
      case 'RESOLVED': return 'success';
      case 'CLOSED': return 'default';
      case 'DUPLICATE': return 'info';
      case 'INVALID': return 'secondary';
      default: return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: IncidentPriority) => {
    switch (priority) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warning';
      case 'HIGH': return 'error';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status: IncidentStatus) => {
    switch (status) {
      case 'OPEN': return <BugReport />;
      case 'IN_PROGRESS': return <Schedule />;
      case 'RESOLVED': return <CheckCircle />;
      case 'CLOSED': return <Cancel />;
      case 'DUPLICATE': return <Assignment />;
      case 'INVALID': return <Warning />;
      default: return <Info />;
    }
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get issue types
  const getIssueTypes = () => [
    'Incorrect Route',
    'Missing Route',
    'Wrong Travel Time',
    'Incorrect Station Name',
    'Missing Station',
    'Wrong Station Location',
    'Incorrect Fare',
    'Service Not Available',
    'Other'
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Incident Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Controls */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={loadIncidents}
        >
          Refresh
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filters
        </Button>
        
        {Object.values(filters).some(filter => filter) && (
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
        )}
        
        <TextField
          size="small"
          placeholder="Search incidents..."
          value={filters.searchTerm}
          onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ mb: 2, p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                    <MenuItem value="RESOLVED">Resolved</MenuItem>
                    <MenuItem value="CLOSED">Closed</MenuItem>
                    <MenuItem value="DUPLICATE">Duplicate</MenuItem>
                    <MenuItem value="INVALID">Invalid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    label="Priority"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="CRITICAL">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Issue Type</InputLabel>
                  <Select
                    value={filters.issueType}
                    onChange={(e) => handleFilterChange('issueType', e.target.value)}
                    label="Issue Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    {getIssueTypes().map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={filters.platform}
                    onChange={(e) => handleFilterChange('platform', e.target.value)}
                    label="Platform"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="web">Web</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>City</InputLabel>
                  <Select
                    value={filters.cityId}
                    onChange={(e) => handleFilterChange('cityId', e.target.value)}
                    label="City"
                  >
                    <MenuItem value="">All</MenuItem>
                    {cities.map(city => (
                      <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="User Email"
                  value={filters.userEmail}
                  onChange={(e) => handleFilterChange('userEmail', e.target.value)}
                />
              </Grid>
            </Grid>
        </Paper>
      </Collapse>

      {/* Incidents Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Issue Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>User Email</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {incidents.map((incident) => (
              <React.Fragment key={incident.id}>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {incident.id?.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {incident.issueType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(incident.status)}
                      label={incident.status}
                      color={getStatusColor(incident.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={incident.priority}
                      color={getPriorityColor(incident.priority) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {incident.userEmail || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={incident.platform || 'N/A'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(incident.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => setExpandedIncident(
                            expandedIncident === incident.id ? null : incident.id || null
                          )}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(incident)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(incident.id!)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
                
                {/* Expanded Row */}
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 0 }}>
                    <Collapse in={expandedIncident === incident.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Description
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {incident.description}
                            </Typography>
                            
                            {incident.metadata && Object.keys(incident.metadata).length > 0 && (
                              <>
                                <Typography variant="subtitle2" gutterBottom>
                                  Metadata
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                  {Object.entries(incident.metadata).map(([key, value]) => (
                                    <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      <strong>{key}:</strong> {JSON.stringify(value)}
                                    </Typography>
                                  ))}
                                </Box>
                              </>
                            )}
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Details
                            </Typography>
                            <Stack spacing={1}>
                              <Typography variant="body2">
                                <strong>ID:</strong> {incident.id}
                              </Typography>
                              <Typography variant="body2">
                                <strong>User Agent:</strong> {incident.userAgent || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>App Version:</strong> {incident.appVersion || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>IP Address:</strong> {incident.ipAddress || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Updated:</strong> {formatDate(incident.updatedAt)}
                              </Typography>
                              {incident.resolvedAt && (
                                <Typography variant="body2">
                                  <strong>Resolved:</strong> {formatDate(incident.resolvedAt)}
                                </Typography>
                              )}
                              {incident.adminNotes && (
                                <Typography variant="body2">
                                  <strong>Admin Notes:</strong> {incident.adminNotes}
                                </Typography>
                              )}
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {incidents.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No incidents found
          </Typography>
        </Box>
      )}

      {/* Edit/View Modal */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Incident' : 'View Incident'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Issue Type"
                  value={formData.issueType}
                  onChange={(e) => setFormData(prev => ({ ...prev, issueType: e.target.value }))}
                  disabled={isEditMode}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="User Email"
                  value={formData.userEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, userEmail: e.target.value }))}
                  disabled={isEditMode}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={isEditMode}
                  multiline
                  rows={3}
                  required
                />
              </Grid>
              
              {isEditMode && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as IncidentStatus }))}
                        label="Status"
                      >
                        <MenuItem value="OPEN">Open</MenuItem>
                        <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                        <MenuItem value="RESOLVED">Resolved</MenuItem>
                        <MenuItem value="CLOSED">Closed</MenuItem>
                        <MenuItem value="DUPLICATE">Duplicate</MenuItem>
                        <MenuItem value="INVALID">Invalid</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={formData.priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as IncidentPriority }))}
                        label="Priority"
                      >
                        <MenuItem value="LOW">Low</MenuItem>
                        <MenuItem value="MEDIUM">Medium</MenuItem>
                        <MenuItem value="HIGH">High</MenuItem>
                        <MenuItem value="CRITICAL">Critical</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Admin Notes"
                      value={formData.adminNotes}
                      onChange={(e) => setFormData(prev => ({ ...prev, adminNotes: e.target.value }))}
                      multiline
                      rows={3}
                      placeholder="Add notes about this incident..."
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            {isEditMode && (
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={20} /> : 'Update'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Incidents;
